import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';
import { MarkupText } from '@/components/MarkupText';
import { PUBLIC_CONTAINER } from '@/components/PublicContainer';
import { formatDate } from './NewsResearchList';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsResearchDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/api/public/news-research/${slug}`)
      .then((r) => { setItem(r.data); document.title = `${r.data.title} · Sergeant TAP`; })
      .catch(() => setItem(null));
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col" data-testid="news-detail-page">
      <PublicHeader />
      <main className="flex-1">
        {item === undefined ? (
          <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
        ) : item === null ? (
          <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="news-not-found">
            <h1 className="text-2xl font-bold text-slate-900">Article not found</h1>
            <p className="mt-2 text-slate-500">This item may be unpublished or the link is incorrect.</p>
            <Link to="/news-research" className="mt-6 inline-flex items-center gap-1.5 text-brand font-semibold">
              <ArrowLeft className="w-4 h-4" /> Back to News &amp; Research
            </Link>
          </div>
        ) : (
          <article className={`${PUBLIC_CONTAINER} py-12`} data-testid="news-article">
            <button onClick={() => navigate('/news-research')} data-testid="news-back"
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> News &amp; Research
            </button>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-bold tracking-wider uppercase text-brand" data-testid="news-category">{item.category}</span>
              {item.published_date && <span className="text-xs text-slate-400">· {formatDate(item.published_date)}</span>}
            </div>
            <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight" data-testid="news-title">{item.title}</h1>
            {item.summary && <p className="mt-5 text-lg text-slate-600 leading-relaxed" data-testid="news-summary">{item.summary}</p>}

            {item.image_url && item.image_url.trim() && (
              <div className="mt-6 rounded-xl overflow-hidden border border-slate-200 bg-slate-100" data-testid="news-image">
                <img src={item.image_url} alt={item.image_alt || item.title}
                  className="w-full h-auto max-h-[360px] object-cover" loading="lazy" />
              </div>
            )}

            <div className="mt-8 border-t border-slate-200 pt-6" data-testid="news-body">
              <MarkupText text={item.body} />
            </div>

            {(item.resource_links || []).length > 0 && (
              <div className="mt-10 border-t border-slate-200 pt-6" data-testid="news-resources">
                <h2 className="text-sm font-bold tracking-wide uppercase text-slate-500 mb-4">Resources</h2>
                <div className="flex flex-wrap gap-3">
                  {item.resource_links.map((l, i) => (
                    <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                      data-testid={`news-resource-${i}`}
                      className="inline-flex items-center gap-2 border border-slate-300 hover:border-brand hover:text-brand text-slate-700 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors">
                      {l.label} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </article>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
